import * as React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { useApolloClient, useQuery } from '@apollo/client';

import {
  GetProperty,
  GetPropertyVariables,
} from '../../../__types__/generated/GetProperty';
import { GET_PROPERTY_QUERY } from '../../../../client/__graphql__/queries/getProperty';

import { Menu, Footer } from '@pm/gui';

import { GlobalStyles } from '../../../../client/GlobalStyles';

import NoMatch from '../NoMatch';

import { Container } from './layout/Container';
import { Top } from './layout/Top';
import { MapBar } from './layout/MapBar';
import { TextSection } from './layout/TextSection';

import { TileDetails, TileButton } from './ui/Tile';
import { TileViewing } from './ui/TileViewing';
import { BrokerItem } from './ui/BrokerItem';
import { TileGallery } from './ui/TileGallery';
import { SiteContext } from '../../utils/context/SiteContext';
import { FacebookIcon } from '../../ui/icons/Facebook';
import { useScrolled } from '../../utils/hooks/useScrolled';
import { SoldPriceForm } from './ui/SoldpriceForm';

import { Seo } from '../../utils/Seo';
import { currency } from '../../utils/currency';

import { ThemeName } from '../../../../client/theme';
import { PreloadImage } from '../../utils/PreloadImage';

const Index: React.FC<{ themeName: ThemeName }> = React.memo(
  ({ themeName }) => {
    const client = useApolloClient();

    const params = useParams<{ urltag: string; page: string; sguid: string }>();

    const { galleryOpen, setGalleryOpen } = React.useContext(SiteContext);

    const [scrolled] = useScrolled({
      offsetTop: 200,
    });

    const { data, loading, error } = useQuery<
      GetProperty,
      GetPropertyVariables
    >(GET_PROPERTY_QUERY, {
      fetchPolicy: 'cache-and-network',
      variables:
        params?.page === 'preview'
          ? { input: { sguid: params?.sguid, preview: true } }
          : { input: { sguid: params?.sguid } },
    });

    if (error?.graphQLErrors?.length && error.graphQLErrors?.length > 0) {
      if (error.graphQLErrors[0]?.extensions?.code === '404') {
        return <NoMatch />;
      }
    }

    if (!data || loading) {
      return null;
    }

    const boligvelgerLink = (args: { pricelist: boolean }) => {
      if (
        data?.getProperty?.newDevelopment?.estatePicker?.url &&
        !args.pricelist
      ) {
        return data?.getProperty?.newDevelopment?.estatePicker?.url;
      } else {
        return `https://privatmegleren.no/prisliste/${params.sguid}`;
      }
    };

    return (
      <>
        <GlobalStyles />
        <GlobalPageStyles />
        <Seo
          title={`${data?.getProperty?.description} - PrivatMegleren`}
          description={data?.getProperty?.description}
          image={
            data?.getProperty?.sold
              ? `https://privatmegleren-objektside.reeltime.no/api/soldimage/${params.sguid}`
              : data?.getProperty?.images?.list &&
                data?.getProperty?.images?.list[0]?.url
          }
        />
        <PreloadImage
          href={
            (data?.getProperty?.images?.list &&
              data?.getProperty?.images?.list[0]?.url) ||
            ''
          }
        />
        <Menu
          className={
            !galleryOpen
              ? themeName === 'nybyggLightOld'
                ? `header-menu white-menu`
                : `header-menu`
              : 'header-hide'
          }
          client={client}
          config={{ transparent: !scrolled, vistopp: false }}
        />

        <Top />
        {data?.getProperty?.sold ? (
          <SoldPriceMobile>
            <SoldPriceForm data={data} />
          </SoldPriceMobile>
        ) : null}

        {/* <MapBar /> */}
        <Container>
          <TilesGrid style={{ marginTop: '10px' }}>
            <div className="col-span-2 row-span-2">
              <TileDetails>
                <span className="location">
                  {data?.getProperty?.location?.county} -{' '}
                  {data?.getProperty?.location?.municipalityArea}
                </span>
                <h1 className="address">{data?.getProperty?.address}</h1>
                <Details>
                  <span className="details price">Prisantydning</span>
                  {data?.getProperty?.price?.info?.price &&
                  data?.getProperty?.price?.info?.priceTo &&
                  data?.getProperty?.price?.info?.price !==
                    data?.getProperty?.price?.info?.priceTo ? (
                    <span className="details-value price">
                      {currency({
                        number: Number(data?.getProperty?.price?.info?.price),
                        seperator: '.',
                      })}
                      ,- -{' '}
                      {currency({
                        number: Number(data?.getProperty?.price?.info?.priceTo),
                        seperator: '.',
                      })}
                      ,-
                    </span>
                  ) : (
                    <span className="details-value price">
                      {currency({
                        number: Number(data?.getProperty?.price?.info?.price),
                        seperator: '.',
                      })}
                      ,-
                    </span>
                  )}

                  {data?.getProperty?.price?.info?.commonShareDebt ? (
                    <>
                      <span className="details">Fellesgjeld</span>
                      {data?.getProperty?.price?.info?.commonShareDebt &&
                      data?.getProperty?.price?.info?.commonShareDebtTo &&
                      data?.getProperty?.price?.info?.commonShareDebt !==
                        data?.getProperty?.price?.info?.commonShareDebtTo ? (
                        <span className="details-value">
                          {currency({
                            number: Number(
                              data?.getProperty?.price?.info?.commonShareDebt
                            ),
                            seperator: '.',
                          })}
                          ,- -{' '}
                          {currency({
                            number: Number(
                              data?.getProperty?.price?.info?.commonShareDebtTo
                            ),
                            seperator: '.',
                          })}
                          ,-
                        </span>
                      ) : (
                        <span className="details-value">
                          {currency({
                            number: Number(
                              data?.getProperty?.price?.info?.commonShareDebt
                            ),
                            seperator: '.',
                          })}
                          ,-
                        </span>
                      )}
                    </>
                  ) : null}
                  {data?.getProperty?.price?.info?.commonCosts ? (
                    <>
                      <span className="details">Felleskostnader</span>
                      {data?.getProperty?.price?.info?.commonCosts &&
                      data?.getProperty?.price?.info?.commonCostsTo ? (
                        <span className="details-value">
                          {currency({
                            number: Number(
                              data?.getProperty?.price?.info?.commonCosts
                            ),
                            seperator: '.',
                          })}
                          ,- -{' '}
                          {currency({
                            number: Number(
                              data?.getProperty?.price?.info?.commonCostsTo
                            ),
                            seperator: '.',
                          })}
                          ,-
                        </span>
                      ) : (
                        <span className="details-value">
                          {currency({
                            number: Number(
                              data?.getProperty?.price?.info?.commonCosts
                            ),
                            seperator: '.',
                          })}
                          ,-
                        </span>
                      )}
                    </>
                  ) : null}
                  {data.getProperty?.area?.prom ? (
                    <>
                      <span className="details">Primærrom</span>
                      {data?.getProperty?.area?.prom &&
                      data?.getProperty?.area.promTo &&
                      data?.getProperty?.area?.prom !==
                        data?.getProperty?.area.promTo ? (
                        <span className="details-value">
                          {data?.getProperty?.area?.prom}m<sup>2</sup> -{' '}
                          {data?.getProperty?.area?.promTo}m<sup>2</sup>
                        </span>
                      ) : (
                        <span className="details-value">
                          {data?.getProperty?.area?.prom}m<sup>2</sup>
                        </span>
                      )}
                    </>
                  ) : null}
                  {data.getProperty?.area?.bra ? (
                    <>
                      <span className="details">Bruksareal</span>
                      {data?.getProperty?.area?.bra &&
                      data?.getProperty?.area.braTo &&
                      data?.getProperty?.area?.bra !==
                        data?.getProperty?.area.braTo ? (
                        <span className="details-value">
                          {data?.getProperty?.area?.bra}m<sup>2</sup> -{' '}
                          {data?.getProperty?.area?.braTo}m<sup>2</sup>
                        </span>
                      ) : (
                        <span className="details-value">
                          {data?.getProperty?.area?.bra}m<sup>2</sup>
                        </span>
                      )}
                    </>
                  ) : null}
                  {data.getProperty?.bedrooms ? (
                    data?.getProperty.bedroomsTo ? (
                      <>
                        <span className="details">Soverom</span>
                        <span className="details-value">
                          {data?.getProperty.bedrooms} -{' '}
                          {data.getProperty.bedroomsTo}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="details">Soverom</span>
                        <span className="details-value">
                          {data?.getProperty?.bedrooms}
                        </span>
                      </>
                    )
                  ) : null}
                  {data.getProperty?.rooms ? (
                    <>
                      <span className="details">Rom</span>
                      <span className="details-value">
                        {data?.getProperty?.rooms}
                      </span>
                    </>
                  ) : null}
                  <span
                    className="facebook-share"
                    onClick={() => {
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=http://www.privatmegleren.no/${params.sguid}`,
                        '_blank'
                      );
                    }}
                  >
                    <FacebookIcon />
                  </span>
                </Details>
              </TileDetails>
            </div>
            <div>
              {data?.getProperty?.hovedoppdrag !== null &&
              !data?.getProperty?.sold ? (
                <TileButton
                  className="meldinteressse"
                  as="a"
                  rel="noopener"
                  href={`https://privatmegleren.no/meldinteresse/${params.sguid}`}
                >
                  Meld interesse
                </TileButton>
              ) : (
                <TileGallery
                  className={data?.getProperty?.sold ? 'full-height' : ''}
                  image={
                    (data?.getProperty?.images?.list &&
                      data?.getProperty?.images?.list[5] &&
                      data?.getProperty?.images?.list[5]?.url) ||
                    ''
                  }
                  onClick={() => {
                    setGalleryOpen(true);
                  }}
                />
              )}
            </div>
            {data?.getProperty?.sold ? (
              <div>
                <TileButton className="sold">Solgt</TileButton>
              </div>
            ) : (
              <div>
                <TileViewing />
              </div>
            )}

            <div className="col-span-2">
              <TileGallery
                image={
                  (data?.getProperty?.images?.list &&
                    data?.getProperty?.images?.list[3]?.url) ||
                  ''
                }
                onClick={() => {
                  setGalleryOpen(true);
                  window.scroll(0, 0);
                }}
              />
            </div>
          </TilesGrid>
          <TileButtonsWrapper>
            {!data.getProperty?.sold &&
            data.getProperty?.hovedoppdrag !== -1 ? (
              <TileButton
                className="small"
                as="a"
                rel="noopener"
                href={`${data?.getProperty?.bid?.link?.url}`}
              >
                Kjøp
              </TileButton>
            ) : null}
            {!data.getProperty?.sold &&
            data?.getProperty?.hovedoppdrag === null ? (
              <TileButton
                className="small"
                as="a"
                rel="noopener"
                href={
                  data?.getProperty?.documents?.list &&
                  data?.getProperty?.documents?.list.length > 0
                    ? `https://privatmegleren.no/meldinteresse/${params.sguid}`
                    : `https://privatmegleren.no/registrerinteressent/${params.sguid}`
                }
              >
                {data?.getProperty?.documents?.list &&
                data?.getProperty?.documents?.list.length > 0
                  ? 'Meld interesse'
                  : 'Bestill salgsoppgave'}
              </TileButton>
            ) : null}

            {data?.getProperty?.documents?.list &&
            data.getProperty.documents.list.length > 0 &&
            !data?.getProperty?.sold ? (
              <TileButton
                className="small"
                as="a"
                rel="noopener"
                href={`https://privatmegleren.no/dokumenter/${params.sguid}`}
              >
                Salgsoppgave/dokumenter
              </TileButton>
            ) : null}

            {data?.getProperty?.newDevelopment?.showPriceList ? (
              <TileButton
                as="a"
                className="large"
                rel="noopener"
                href={boligvelgerLink({ pricelist: true })}
              >
                Prisliste
              </TileButton>
            ) : null}
            {data?.getProperty?.newDevelopment?.showPriceList &&
            data.getProperty.newDevelopment?.estatePicker?.label &&
            data.getProperty.newDevelopment?.estatePicker?.url ? (
              <TileButton
                as="a"
                className="large"
                rel="noopener"
                href={boligvelgerLink({ pricelist: false })}
              >
                {data.getProperty.newDevelopment?.estatePicker?.label}
              </TileButton>
            ) : null}

            {data?.getProperty?.office?.info?.hasSoldPrice &&
            data?.getProperty?.sold ? (
              <TileButton
                className="large"
                as="a"
                rel="noopener"
                href={`https://privatmegleren.no/${data?.getProperty?.office?.info?.urltag}/${params.sguid}/${data.getProperty.permalink}/solgtpris`}
              >
                Ønsker du å vite eiendommens endelige salgssum?
              </TileButton>
            ) : null}
            <TileButton
              className="large"
              as="a"
              rel="noopener"
              href={`https://privatmegleren.no/${data?.getProperty?.office?.info?.urltag}/verdivurdering`}
            >
              Ønsker du verdivurdering?
            </TileButton>
            {data?.getProperty?.brokers?.list &&
              data?.getProperty?.brokers?.list?.map((item) => {
                return (
                  <div key={item?.id} className="row">
                    <BrokerItem
                      image={item?.image?.full}
                      name={item?.name}
                      path={item?.path}
                      phone={item?.cellphone}
                      title={item?.title}
                      office={item?.office?.info?.name}
                      urltag={item?.office?.info?.urltag}
                    />
                  </div>
                );
              })}
          </TileButtonsWrapper>
          {!data.getProperty?.sold ? <TextSection data={data} /> : null}
        </Container>
        <Footer />
      </>
    );
  }
);

export default Index;

const TileButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 10px;

  @media all and (max-width: 960px) {
    margin-top: 10px;
  }
`;

const TilesGrid = styled.div`
  display: grid;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  width: 100%;

  @media all and (min-width: 550px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 200px 1fr max-content;

    .col-span-2 {
      grid-column: span 2;
    }
    .row-span-2 {
      grid-row: span 2;
    }
  }

  @media all and (min-width: 960px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 200px 1fr max-content;

    .col-span-2 {
      grid-column: span 2;
    }
    .row-span-2 {
      grid-row: span 2;
    }
  }
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-row-gap: 10px;
  grid-column-gap: 20px;

  .facebook-share {
    display: block;
    margin-top: 30px;
    width: 15px;
    height: 15px;

    svg {
      width: inherit;
      height: inherit;
    }
  }
`;

const GlobalPageStyles = createGlobalStyle`
  body {
    color: rgb(255, 255, 255);
    line-height: 1.4;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 400;
    color: ${({ theme }) => theme?.colors?.primary};
  }
  p {
    font-weight: 200;
  }

  .desktop {
      @media all and (max-width: 960px) {
          display: none !important;
      }
  }

  .mobile {
      @media all and (min-width: 960px) {
          display: none !important;
      }
  }

  .header-hide {
      display: none;
  }

  .header-menu {
    transition: background .15s ease-in-out;
    
    &.menu-background {
      background: #171719;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    }

    &.white-menu {
      .large-menu {
        color: #000;
      }

      .menu-hamburger-icon {
        a > span {
          background: #fff;
          @media all and (min-width: 960px) {
            background: #000;
          }
        }
      }

      .menu-search-icon {
        a {
          svg {
            fill: #fff;
            @media all and (min-width: 960px) {
              fill: #000;
            }
       
          }
        }
      }

      .large-logo {
        svg {
          path {
            fill: #fff;
            stroke: transparent;
          }
          rect {
            fill: transparent;
            stroke: #fff;
          }
        }
      }

      .small-logo {
        svg {
          path {
            fill: #fff;
            stroke: transparent;

          
          }
          rect {
            fill: transparent;
            stroke: #fff;

          }
        }
      }

      &.menu-open {
        .large-logo {
          svg {
            path {
              fill: #fff;
              stroke: transparent;
            }
            rect {
              fill: transparent;
              stroke: #fff;
            }

          }
        }
        .small-logo {
          svg {
            path {
              fill: #fff;
              stroke: transparent;
            }
            rect {
              fill: transparent;
              stroke: #fff;
            }

          }
        }
        .menu-hamburger-icon {
          a > span {
            background: #fff;
          }
        }
        .menu-search-icon {
          a {
            svg {
              fill: #fff;
            }
          }
        }
      }

       &.menu-background {
        background: #fff;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);

        .small-logo {
          svg {
            path {
              fill: #000 !important;
            }
            rect {
              stroke: #000 !important;
            }
          }
        }

        .menu-hamburger-icon {
        a > span {
          background: #000;
         
        }
      }

      .menu-search-icon {
        a {
          svg {
            fill: #000;
          }
        }
      }

      .large-logo {
        svg {
          path {
            fill: #000;
          }
          rect {
            stroke: #000;
          }
        }
      }

      }
    }
  }
`;

const SoldPriceMobile = styled.div`
  display: none;
  padding: 30px;
  width: 100%;

  @media all and (max-width: 960px) {
    display: block;
  }
`;
